import BaseNotificationHandler from './baseHandler.js';

/**
 * Comment Notification Handler
 * Handles comment notifications
 */
class CommentHandler extends BaseNotificationHandler {
  /**
   * Parse notification data and normalize ID fields to integers
   * @param {Object} notification - The notification object
   * @returns {Object} Parsed and normalized data object
   */
  parseData(notification) {
    const parsedData = super.parseData(notification);

    // Normalize ID fields to integers (backend sometimes sends them as strings)
    const idFields = ['comment_id', 'reply_id', 'post_id', 'content_id', 'sender_id', 'user_id'];
    idFields.forEach((field) => {
      if (parsedData[field] !== undefined && parsedData[field] !== null) {
        const intVal = parseInt(parsedData[field], 10);
        if (!isNaN(intVal)) {
          parsedData[field] = intVal;
        }
      }
      // Also check nested data object
      if (parsedData.data?.[field] !== undefined && parsedData.data?.[field] !== null) {
        const intVal = parseInt(parsedData.data[field], 10);
        if (!isNaN(intVal)) {
          parsedData.data[field] = intVal;
        }
      }
    });

    return parsedData;
  }

  getIcon(notification, parsedData) {
    return "💬";
  }

  getMainText(notification, parsedData) {
    const senderName = this.getSenderInfo(notification, parsedData) || 'Someone';
    // Check if this is a comment reply
    if (notification.type === 'comment_reply' || parsedData.reply_id || parsedData.data?.reply_id) {
      return `${senderName} replied to your comment`;
    }

    return `${senderName} commented on your post`;
  }

  getDetails(notification, parsedData) {
    const details = [];

    // Check if this is a comment reply
    const isReply = notification.type === 'comment_reply' || parsedData.reply_id || parsedData.data?.reply_id;

    if (isReply) {
      // Show reply text preview if available
      if (parsedData.reply_text || parsedData.data?.reply_text) {
        const preview = (parsedData.reply_text || parsedData.data?.reply_text || '').substring(0, 50);
        if (preview) {
          details.push(`"${preview}${preview.length >= 50 ? '...' : ''}"`);
        }
      }
    } else {
      // Show comment preview if available
      if (parsedData.comment_preview || parsedData.comment_content || parsedData.comment_text) {
        const preview = (parsedData.comment_preview || parsedData.comment_content || parsedData.comment_text || '').substring(0, 50);
        if (preview) {
          details.push(`"${preview}${preview.length >= 50 ? '...' : ''}"`);
        }
      }
    }

    return details;
  }

  getThumbnail(notification, parsedData) {
    // For comments, try to get post image
    if (parsedData.post_image) return parsedData.post_image;
    if (parsedData.image) return parsedData.image;
    if (parsedData.media?.[0]?.url) return parsedData.media[0].url;
    if (parsedData.data?.post_image) return parsedData.data.post_image;
    if (parsedData.data?.image) return parsedData.data.image;
    return super.getThumbnail(notification, parsedData);
  }

  getActionUrl(notification, parsedData) {
    // Check parsedData.action_url first (outer level)
    if (parsedData.action_url) {
      return parsedData.action_url;
    }

    // Check nested data.action_url (from double-encoded JSON)
    if (parsedData.data?.action_url) {
      return parsedData.data.action_url;
    }

    // Check notification.action_url as fallback
    if (notification.action_url) {
      return notification.action_url;
    }

    // Build post URL from post_id if available (for comments and comment replies)
    const postId = parsedData.post_id || parsedData.data?.post_id;
    if (postId) {
      return `/post/${postId}`;
    }

    return null;
  }

  /**
   * Get post ID from notification data
   * @param {Object} notification - The notification object
   * @param {Object} parsedData - Parsed notification data
   * @returns {number|null} Post ID or null
   */
  getPostId(notification, parsedData) {
    // For comments and comment replies, post_id should be available
    return parsedData.post_id || parsedData.data?.post_id;
  }

  /**
   * Get comment ID from notification data
   * @param {Object} notification - The notification object
   * @param {Object} parsedData - Parsed notification data
   * @returns {number|null} Comment ID or null
   */
  getCommentId(notification, parsedData) {
    return parsedData.comment_id || parsedData.data?.comment_id ||
      parsedData.reply_id || parsedData.data?.reply_id ||
      parsedData.content_id || parsedData.data?.content_id;
  }

  handleClick(notification, parsedData, navigate, onClose, openPostModal = null) {
    // If openPostModal is provided, use it to open the PostDetailModal
    if (openPostModal) {
      const postId = this.getPostId(notification, parsedData);
      const commentId = this.getCommentId(notification, parsedData);
      if (postId) {
        openPostModal(postId, commentId);
        if (onClose) onClose();
        return;
      }
    }

    // Fallback to navigation if openPostModal is not available or postId is not found
    const actionUrl = this.getActionUrl(notification, parsedData);
    if (actionUrl) {
      if (actionUrl.startsWith('http://') || actionUrl.startsWith('https://')) {
        window.location.href = actionUrl;
      } else {
        navigate(actionUrl);
      }
      if (onClose) onClose();
    }
  }
}

export default CommentHandler;

